/**
 * Core HTTP client — TIDE VÍDEO standalone (Fase 2.3 / ADR-VID-005).
 *
 * Adaptado de frontend/src/lib/api.ts: Bearer via token NK único (não JWT de
 * usuário), sem /auth/me, sem adminApi (não existe conceito de tenant/admin
 * no standalone). 401 limpa o token e redireciona /credencial.
 */

import { clearNkToken, NK_TOKEN_KEY } from "@/lib/auth-token";

const API_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001/api/v1";

/** FastAPI 422 de validação manda detail como LISTA de {loc, msg}; erros de
 * negócio mandam string ou {code, message}. Normaliza os três pra mensagem. */
function detailToMessage(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => {
        const loc = Array.isArray(d?.loc) ? d.loc.slice(1).join(".") : "";
        return loc ? `${loc}: ${d?.msg ?? "inválido"}` : (d?.msg ?? "");
      })
      .filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }
  const msg = (detail as { message?: string } | null)?.message;
  return msg || "Request failed";
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public info?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token =
    typeof window !== "undefined" ? localStorage.getItem(NK_TOKEN_KEY) : null;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    if (res.status === 401 && typeof window !== "undefined") {
      clearNkToken();
      window.location.href = "/credencial";
      throw new ApiError(401, "Sessão expirada");
    }
    const body = await res.json().catch(() => ({ detail: "Request failed" }));
    const detail = body.detail;
    const message =
      body.code === "INTEGRITY_VIOLATION"
        ? "Registro duplicado — já existe um cadastro com essa chave."
        : detailToMessage(detail);
    throw new ApiError(res.status, message, detail);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const token =
    typeof window !== "undefined" ? localStorage.getItem(NK_TOKEN_KEY) : null;

  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers,
    body: form,
  });

  if (!res.ok) {
    if (res.status === 401 && typeof window !== "undefined") {
      clearNkToken();
      window.location.href = "/credencial";
      throw new ApiError(401, "Sessão expirada");
    }
    const body = await res.json().catch(() => ({ detail: "Request failed" }));
    const detail = body.detail;
    const message =
      body.code === "INTEGRITY_VIOLATION"
        ? "Registro duplicado — já existe um cadastro com essa chave."
        : detailToMessage(detail);
    throw new ApiError(res.status, message, detail);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(data) }),
  postForm: <T>(path: string, form: FormData) => requestForm<T>(path, form),
  put: <T>(path: string, data: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(data) }),
  patch: <T>(path: string, data: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(data) }),
  delete: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: "DELETE",
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
};

export { ApiError };
